import { ProductAttributeKeys, type ProductAttribute } from "@wttb/product-configurator";
import { useTranslations } from "next-intl";
import React, { ReactNode } from "react";

import { ProductAttributeInfo } from "@/clients/contentful";
import { SequentialNumberingAttribute } from "@/features/product/components/product-attributes/sequential-numbering-attribute";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { AttributePopupInfo } from "./attribute-popup-info";
import {
  MultipleChoiceButtonSelector,
  type MultipleOption,
} from "./multiple-choice-button-selector";
import { OrientationAttribute } from "./orientation-attribute";
import { PagesAttribute } from "./pages-attribute";
import { QuantityAttribute } from "./quantity-attribute";
import { SingleChoiceButtonSelector, type SingleOption } from "./single-choice-button-selector";
import { SizeRunAttribute } from "./size-run-attribute";
import { VersionsAttribute } from "./versions-attribute";

export type FlagType = {
  text: string;
  className?: string;
  icon?: ReactNode;
};

const ProductAttribute: React.FC<{
  attribute: ProductAttribute;
  index: number;
  info?: ProductAttributeInfo;
}> = ({ attribute, index, info }) => {
  const t = useTranslations();
  const { setConfigValue, config, attributesHash, incompatibilities } = useStoreProduct();
  const attributeName = attribute.display || t(`attributes.${attribute.key}`);

  if (!config) {
    return null;
  }

  const attributeOptions = Object.keys(attributesHash[attribute.key])
    .map((valueId) => {
      const { name, value, isNew } = attributesHash[attribute.key][valueId];

      const isConfigHaveIncompatibleValueSelected = (incompatibilities[attribute.key] || []).some(
        (incompatibility) => incompatibility.valueIds.includes(valueId),
      );

      const flags = [];
      if (isNew) {
        flags.push({ text: t("common.text-new"), className: "bg-violet" });
      }

      return {
        id: valueId,
        name,
        value,
        isIncompatible: isConfigHaveIncompatibleValueSelected,
        flags,
      };
    })
    .filter((item) => index === 0 || !item.isIncompatible);

  const onSelect = (selection: string | string[]) => {
    setConfigValue(attribute.key, selection);
  };

  return (
    <div className="w-full py-4">
      <div className="mb-4 flex">
        <p className="text-grey-90 m-0 mr-2 cursor-pointer text-lg font-semibold leading-none md:text-2xl">
          {attributeName}
        </p>
        {info && <AttributePopupInfo data={info} attributeName={attributeName} />}
      </div>

      <div className="text-grey-80 w-full bg-white shadow-lg">
        {attribute.key === ProductAttributeKeys.Quantity ? (
          <QuantityAttribute attribute={attribute} />
        ) : attribute.key === ProductAttributeKeys.SizeRun ? (
          <SizeRunAttribute attribute={attribute} />
        ) : attribute.key === ProductAttributeKeys.SequentialNumbering ? (
          <SequentialNumberingAttribute
            onChange={onSelect}
            attribute={attribute}
            options={attributeOptions as SingleOption[]}
          />
        ) : attribute.key === ProductAttributeKeys.Pages ? (
          <PagesAttribute attribute={attribute} />
        ) : attribute.key === ProductAttributeKeys.Orientation ? (
          <OrientationAttribute
            attribute={attribute}
            options={attributeOptions as SingleOption[]}
          />
        ) : attribute.key === ProductAttributeKeys.Versions ? (
          <VersionsAttribute attribute={attribute} options={attributeOptions as SingleOption[]} />
        ) : attribute.multiple ? (
          <MultipleChoiceButtonSelector
            label={attribute.display || t(`attributes.${attribute.key}`)}
            attribute={attribute}
            options={attributeOptions as MultipleOption[]}
            onChange={onSelect}
          />
        ) : (
          <SingleChoiceButtonSelector
            attribute={attribute}
            options={attributeOptions as SingleOption[]}
            onChange={onSelect}
          />
        )}
      </div>
    </div>
  );
};

const ProductAttributeList: React.FC<{
  attributesInfo: Record<string, ProductAttributeInfo>;
}> = ({ attributesInfo }) => {
  const { attributes } = useStoreProduct();

  if (!attributes?.length) return null;

  return attributes
    .filter((attribute) => !attribute.hidden)
    .map((attribute, index) => (
      <ProductAttribute
        key={attribute.key}
        index={index}
        attribute={attribute}
        info={attributesInfo[attribute.key]}
      />
    ));
};

export const ProductAttributesRenderer = React.memo(ProductAttributeList);
